import React, { useState, useEffect } from 'react';
import { Send, Eye, Loader2 } from 'lucide-react';
import { useAppStore } from '../../store/useAppStore';
import { checkUserCredits } from '../../utils/creditManager';

interface DynamicProFormProps {
  proFormConfig?: any;
  creditCost?: number;
  pageTitle?: string;
  pageDescription?: string;
  exampleMediaUrl?: string;
  exampleMediaType?: string;
  onCancel?: () => void;
  onComplete?: () => void;
  onSubmit?: (data: any) => void;
}

export const DynamicProForm: React.FC<DynamicProFormProps> = ({
  proFormConfig = null,
  creditCost = 10,
  pageTitle = 'Pro Request',
  pageDescription = 'Submit your custom request',
  exampleMediaUrl = '',
  exampleMediaType = 'image',
  onCancel = () => {},
  onComplete = () => {},
  onSubmit
}) => {
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCheckingCredits, setIsCheckingCredits] = useState(true);
  const [hasEnoughCredits, setHasEnoughCredits] = useState(false);
  const [currentCredits, setCurrentCredits] = useState<number | null>(null);
  const { openInsufficientCreditsModalWithAmount, session } = useAppStore();

  // Use the props to avoid unused variable warnings
  const formTitle = pageTitle || proFormConfig?.form_name || 'Pro Request';
  const formDesc = pageDescription || proFormConfig?.form_description || 'Submit your custom request';
  const hasExampleMedia = exampleMediaUrl && exampleMediaType;

  // Check user credits on component mount
  useEffect(() => {
    const performCreditCheck = async () => {
      if (!session) {
        console.warn('⚠️ [CREDIT-CHECK] No session available');
        setIsCheckingCredits(false);
        return;
      }

      try {
        setIsCheckingCredits(true);
        console.log(`💰 [CREDIT-CHECK] Checking credits for pro form - Required: ${creditCost}`);

        const result = await checkUserCredits(session, creditCost);

        if (result.success) {
          setHasEnoughCredits(result.hasCredits || false);
          setCurrentCredits(result.currentCredits || null);

          console.log(`💰 [CREDIT-CHECK] Credits: ${result.currentCredits}, Has enough: ${result.hasCredits}`);

          // Show insufficient credits modal immediately if not enough credits
          if (!result.hasCredits) {
            console.log('❌ [CREDIT-CHECK] Insufficient credits detected on load');
            openInsufficientCreditsModalWithAmount(creditCost);
          }
        } else {
          console.error('❌ [CREDIT-CHECK] Failed:', result.error);
          // On error, allow form to be filled (optimistic UX)
          setHasEnoughCredits(true);
        }
      } catch (error) {
        console.error('💥 [CREDIT-CHECK] Error:', error);
        // On error, allow form to be filled (optimistic UX)
        setHasEnoughCredits(true);
      } finally {
        setIsCheckingCredits(false);
      }
    };

    performCreditCheck();
  }, [session, creditCost, openInsufficientCreditsModalWithAmount]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Final credit check before submission
    if (!session) {
      console.error('❌ [SUBMIT] No session available');
      return;
    }

    // Re-check credits before submission
    console.log('🔄 [SUBMIT] Re-checking credits before submission');
    const creditCheckResult = await checkUserCredits(session, creditCost);

    if (!creditCheckResult.success || !creditCheckResult.hasCredits) {
      console.log('❌ [SUBMIT] Insufficient credits on submit attempt');
      openInsufficientCreditsModalWithAmount(creditCost);
      return;
    }

    setIsSubmitting(true);

    try {
      console.log('📤 [SUBMIT] Processing pro form submission');

      // Process form submission
      if (onSubmit) {
        onSubmit(formData);
      } else {
        // Default completion behavior
        onComplete();
      }

    } catch (error) {
      console.error('💥 [SUBMIT] Pro form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const showDevPreview = import.meta.env.MODE === 'development';

  // Handle cancel action
  const handleCancel = () => {
    onCancel();
  };

  // Show loading state while checking credits
  if (isCheckingCredits) {
    return (
      <div className="card p-6 flex items-center justify-center min-h-[200px]">
        <div className="text-center space-y-3">
          <Loader2 className="w-8 h-8 text-brand animate-spin mx-auto" />
          <p className="text-th-secondary text-sm">Checking your credits...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Dev Mode Preview Button */}
      {showDevPreview && (
        <button
          onClick={() => openInsufficientCreditsModalWithAmount(creditCost)}
          className="absolute top-2 left-2 z-10 p-2 bg-accent text-surface rounded-lg opacity-50 hover:opacity-100 transition-opacity"
          title="Preview Insufficient Credits Modal (Dev Mode)"
        >
          <Eye className="w-4 h-4" />
        </button>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-th-primary mb-4">
            {formTitle}
          </h3>

          {formDesc && (
            <p className="text-th-secondary mb-4">{formDesc}</p>
          )}

          {/* Credit Balance Display */}
          {currentCredits !== null && (
            <div className={`mb-4 p-3 rounded-lg border ${
              hasEnoughCredits
                ? 'bg-status-success/10 border-status-success/30'
                : 'bg-status-error/10 border-status-error/30'
            }`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm font-medium ${
                    hasEnoughCredits ? 'text-status-success' : 'text-status-error'
                  }`}>
                    {hasEnoughCredits ? '✓ Sufficient Credits' : '⚠ Insufficient Credits'}
                  </p>
                  <p className="text-xs text-th-secondary mt-1">
                    Your balance: {currentCredits} credits | Required: {creditCost} credits
                  </p>
                </div>
              </div>
            </div>
          )}

          {hasExampleMedia && (
            <div className="mb-4 p-2 bg-brand/10 border border-brand/20 rounded">
              <p className="text-brand text-xs">Example media available</p>
            </div>
          )}
          
          <div className="space-y-4">
            <div>
              <label className="block text-th-secondary text-sm font-medium mb-2">
                Request Details
              </label>
              <textarea
                value={formData.details || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, details: e.target.value }))}
                className="w-full px-3 py-2 border border-subtle rounded-lg bg-elevated text-th-primary"
                rows={4}
                placeholder="Describe your request..."
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmitting || !hasEnoughCredits}
            className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-brand text-surface rounded-lg font-medium hover:bg-brand/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all mt-6"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Submitting...</span>
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                <span>Submit Request ({creditCost} credits)</span>
              </>
            )}
          </button>

          {!hasEnoughCredits && (
            <p className="text-status-error text-xs text-center mt-2">
              Insufficient credits. Please recharge in the Hyper app.
            </p>
          )}
          
          <button
            type="button"
            onClick={handleCancel}
            className="w-full mt-3 px-6 py-3 bg-elevated border border-subtle text-th-secondary rounded-lg font-medium hover:text-th-primary transition-all"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};